import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap, delay } from 'rxjs/operators';
import { DataService } from 'src/api/services';
import { AbstractDataService } from './data.service';

interface CacheEntry {
  observable: Observable<any>;
  timestamp: number;
  subscribers: number;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardDataCacheService {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_TTL = 10000; // 5 seconds - adjust as needed
  private cleanupInterval: any;

  constructor(private dataService: AbstractDataService) {
    // Periodic cleanup of stale entries
    this.cleanupInterval = setInterval(() => this.cleanup(), 10000);
  }

  /**
   * Get dashboard data with deduplication
   * Multiple calls with the same params will share the same request
   */
  getDashboardData(params: DataService.DataDashboardGetsParams): Observable<any> {
    const key = this.generateKey(params);
    const now = Date.now();

    // Check if we have a valid cached entry
    const cached = this.cache.get(key);
    if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
      cached.subscribers++;
      console.log(`🔄 [CACHE HIT] MERGING REQUEST into existing call`);
      console.log(`   📊 Request: Dashboard ${params.dashboardId}, ${params.requests?.length || 0} time series`);
      console.log(`   👥 Total subscribers sharing this request: ${cached.subscribers}`);
      console.log(`   ⏱️  Cache age: ${now - cached.timestamp}ms`);
      //console.log(`   🔑 Key: ${key.substring(0, 80)}...`);

      // Add staggered delay for subsequent subscribers to prevent UI blocking
      const delayMs = (cached.subscribers - 1) * 50; // 50ms per additional subscriber
      if (delayMs > 0) {
        console.log(`   ⏰ Staggering emission by ${delayMs}ms to prevent UI blocking`);
        return cached.observable.pipe(delay(delayMs));
      }
      return cached.observable;
    }

    // Create new request
    console.log(`🆕 [CACHE MISS] Creating NEW request to server`);
    console.log(`   📊 Request: Dashboard ${params.dashboardId}, ${params.requests?.length || 0} time series`);
    console.log(`   🔑 Key: ${key.substring(0, 80)}...`);

    const observable = this.dataService.dashboardGets(params).pipe(
      shareReplay(1), // Share the result with all subscribers
      tap({
        next: (data) => {
          const entry = this.cache.get(key);
          if (entry && entry.subscribers > 1) {
            console.log(`✅ [DATA RECEIVED] Broadcasting to ${entry.subscribers} widgets (${entry.subscribers - 1} merged)`);
            //console.log(`   📊 Dashboard ${params.dashboardId}`);
          } else {
            console.log(`✅ [DATA RECEIVED] Single widget request completed`);
            //console.log(`   📊 Dashboard ${params.dashboardId}`);
          }
        },
        complete: () => {
          // Decrease subscriber count on completion
          const entry = this.cache.get(key);
          if (entry) {
            entry.subscribers--;
            if (entry.subscribers === 0) {
              console.log(`✔️  [REQUEST COMPLETE] All subscribers received data`);
            }
          }
        },
        error: (err) => {
          console.error(`❌ [REQUEST ERROR] Removing from cache due to error:`, err.message || err);
          // Remove from cache on error
          this.cache.delete(key);
        }
      })
    );

    // Store in cache
    this.cache.set(key, {
      observable,
      timestamp: now,
      subscribers: 1
    });

    return observable;
  }

  /**
   * Generate a unique key for the request parameters
   */
  private generateKey(params: DataService.DataDashboardGetsParams): string {
    // Create a deterministic string representation of the request
    return JSON.stringify({
      customerId: params.customerId,
      dashboardId: params.dashboardId,
      requests: params.requests?.map(r => ({
        period: r.period,
        uri: r.timeSeries?.single?.variable?.uri,
        tagMatches: r.timeSeries?.single?.variable?.tagMatches,
        transforms: r.timeSeries?.single?.variable?.transforms,
        virtualTimeSeriesId: r.timeSeries?.virtualTimeSeriesId,
        siteUri: r.siteUri,
        siteType: r.siteType,
        type: r.type,
        timeZoneOffset: r.timeZoneOffset
      }))
    });
  }

  /**
   * Clean up stale cache entries
   */
  private cleanup(): void {
    const now = Date.now();
    const toDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if ((now - entry.timestamp) > this.CACHE_TTL && entry.subscribers === 0) {
        toDelete.push(key);
      }
    });

    if (toDelete.length > 0) {
      console.log(`🧹 [CACHE CLEANUP] Removing ${toDelete.length} stale cache entries`);
      toDelete.forEach(key => {
        this.cache.delete(key);
      });
    }
  }

  /**
   * Invalidate all cache entries (useful when dashboard is refreshed)
   */
  invalidateAll(): void {
    const count = this.cache.size;
    console.log(`🗑️  [CACHE INVALIDATE] Clearing all ${count} cache entries`);
    this.cache.clear();
  }

  /**
   * Invalidate specific dashboard cache
   */
  invalidateDashboard(dashboardId: number): void {
    const toDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (key.includes(`"dashboardId":${dashboardId}`)) {
        toDelete.push(key);
      }
    });

    if (toDelete.length > 0) {
      console.log(`🗑️  [CACHE INVALIDATE] Clearing ${toDelete.length} entries for dashboard ${dashboardId}`);
      toDelete.forEach(key => this.cache.delete(key));
    }
  }

  ngOnDestroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }
}
